import React, { useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import api from "../../utils/api";
import {
  FaUser,
  FaWallet,
  FaGift,
  FaMobileAlt,
  FaEnvelope,
  FaClock,
  FaShieldAlt,
  FaKey,
  FaCopy,
  FaCheck,
  FaArrowDown,
  FaArrowUp,
  FaExchangeAlt,
  FaDice,
  FaIdBadge,
  FaCalendarAlt,
} from "react-icons/fa";

export const UserDetails = ({ onClose }) => {
  const { user } = useSelector((state) => state.user);
  const [showPass, setShowPass] = useState(false);
  const [passForm, setPassForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handlePassUpdate = async (e) => {
    e.preventDefault();
    if (passForm.newPassword !== passForm.confirmPassword) return setMsg({ type: "danger", text: "Passwords do not match!" });
    setLoading(true);
    try {
      const res = await api.put("/users/update-password", passForm);
      if (res.data.success) {
        setMsg({ type: "success", text: "Password Updated Successfully!" });
        setPassForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setTimeout(() => { setShowPass(false); setMsg(null); }, 2000);
      }
    } catch (err) { setMsg({ type: "danger", text: err.response?.data?.message || "Failed" }); }
    setLoading(false);
  };

  const hasPassword = !!user?.hasPassword;
  const initial = (user?.username || user?.userId || "U").charAt(0).toUpperCase();

  const copyReferral = () => {
    if (!user?.refer?.referralCode) return;
    navigator.clipboard?.writeText(user.refer.referralCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const detailRows = [
    { icon: <FaIdBadge />, label: "User ID", value: user?.userId },
    { icon: <FaMobileAlt />, label: "Mobile", value: user?.mobile },
    { icon: <FaEnvelope />, label: "Email", value: user?.email },
    {
      icon: <FaCalendarAlt />,
      label: "Member Since",
      value: user?.createdAt
        ? new Date(user.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        : null,
    },
    {
      icon: <FaClock />,
      label: "Last Login",
      value: user?.lastLoginTime ? new Date(user.lastLoginTime).toLocaleString("en-IN") : null,
    },
    { icon: <FaShieldAlt />, label: "Login Method", value: user?.lastLoginMethod },
  ].filter((r) => r.value);

  const quickActions = [
    { to: "/deposit", label: "Deposit", icon: <FaArrowDown />, color: "#198754" },
    { to: "/withdrawal", label: "Withdraw", icon: <FaArrowUp />, color: "#dc3545" },
    { to: "/transactions", label: "Transactions", icon: <FaExchangeAlt />, color: "#0d6efd" },
    { to: "/user-bet-history", label: "Bet History", icon: <FaDice />, color: "#e263d7" },
  ];

  return (
    <div className="container mt-4 mb-5" style={{ maxWidth: 960 }}>
      {onClose && (
        <div className="text-end mb-2">
          <button className="btn btn-sm btn-danger" onClick={onClose}>✕ Close</button>
        </div>
      )}

      {/* Profile Hero */}
      <div className="card border-0 shadow-sm overflow-hidden mb-3">
        <div
          className="p-4 d-flex flex-wrap align-items-center gap-3"
          style={{ background: "linear-gradient(135deg, #1c2740 0%, #3b2a63 100%)" }}
        >
          <div
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 text-white fw-bold"
            style={{
              width: 64,
              height: 64,
              fontSize: 26,
              background: "linear-gradient(135deg, #7b5cff, #e263d7)",
              boxShadow: "0 4px 14px rgba(123,92,255,.4)",
            }}
          >
            {initial}
          </div>
          <div className="flex-grow-1 min-w-0">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h5 className="mb-0 fw-bold text-white text-truncate">{user?.username || "-"}</h5>
              {user?.status && (
                <span className={`badge rounded-pill ${user.status === "active" ? "bg-success" : "bg-secondary"} text-capitalize`}>
                  {user.status}
                </span>
              )}
            </div>
            <div className="text-white-50 small mt-1">
              <FaUser className="me-1" size={11} />
              {user?.userId || "-"}
            </div>
          </div>
          <button
            className="btn btn-sm btn-outline-light d-flex align-items-center gap-2 flex-shrink-0"
            onClick={() => setShowPass(true)}
          >
            <FaKey size={12} /> Change Password
          </button>
        </div>

        {/* Wallet strip */}
        <div className="row g-0 border-top">
          <div className="col-4 p-3 text-center border-end">
            <div className="text-muted small d-flex align-items-center justify-content-center gap-1">
              <FaWallet size={11} /> Balance
            </div>
            <div className="fw-bold fs-5 text-success">
              ₹{user?.wallet?.balance?.toLocaleString("en-IN") || "0"}
            </div>
          </div>
          <div className="col-4 p-3 text-center border-end">
            <div className="text-muted small d-flex align-items-center justify-content-center gap-1">
              <FaGift size={11} /> Bonus
            </div>
            <div className="fw-bold fs-5 text-warning">
              ₹{user?.wallet?.bonusBalance?.toLocaleString("en-IN") || "0"}
            </div>
          </div>
          <div
            className="col-4 p-3 text-center"
            style={{ cursor: user?.refer?.referralCode ? "pointer" : "default" }}
            onClick={copyReferral}
            title="Click to copy"
          >
            <div className="text-muted small">Referral Code</div>
            <div className="fw-bold fs-6 text-primary d-flex align-items-center justify-content-center gap-2">
              {user?.refer?.referralCode || "-"}
              {user?.refer?.referralCode &&
                (copied ? <FaCheck className="text-success" size={12} /> : <FaCopy className="opacity-50" size={12} />)}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="row g-2 mb-3">
        {quickActions.map((a) => (
          <div key={a.to} className="col-6 col-md-3">
            <Link
              to={a.to}
              className="card border-0 shadow-sm text-decoration-none h-100 quick-action"
            >
              <div className="card-body py-3 d-flex align-items-center justify-content-center gap-2">
                <span style={{ color: a.color }}>{a.icon}</span>
                <span className="fw-bold text-dark small">{a.label}</span>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {/* Account Details */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white py-3">
          <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
            <FaUser className="text-primary" /> Account Details
          </h6>
        </div>
        <div className="card-body p-0">
          <div className="row g-0">
            {detailRows.map((row, i) => (
              <div key={row.label} className="col-12 col-md-6">
                <div className="d-flex align-items-center gap-3 p-3 border-bottom">
                  <span
                    className="rounded d-flex align-items-center justify-content-center flex-shrink-0 bg-primary bg-opacity-10 text-primary"
                    style={{ width: 34, height: 34 }}
                  >
                    {row.icon}
                  </span>
                  <div className="min-w-0">
                    <div className="text-muted small">{row.label}</div>
                    <div className="fw-bold text-dark text-truncate">{row.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <Modal show={showPass} onHide={() => setShowPass(false)} centered>
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="fs-5">Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handlePassUpdate}>
            {hasPassword && (
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">Current Password</Form.Label>
                <Form.Control type="password" size="sm" required value={passForm.currentPassword} onChange={e => setPassForm({...passForm, currentPassword: e.target.value})} />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">New Password</Form.Label>
              <Form.Control type="password" size="sm" required value={passForm.newPassword} onChange={e => setPassForm({...passForm, newPassword: e.target.value})} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">Confirm New Password</Form.Label>
              <Form.Control type="password" size="sm" required value={passForm.confirmPassword} onChange={e => setPassForm({...passForm, confirmPassword: e.target.value})} />
            </Form.Group>

            {msg && <Alert variant={msg.type} className="py-2 small mb-3">{msg.text}</Alert>}

            <div className="d-flex gap-2 mt-4">
              <Button variant="primary" size="sm" type="submit" className="w-100 fw-bold" disabled={loading}>
                {loading ? "Updating..." : "Update Password"}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setShowPass(false)}>Cancel</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <style>{`
        .quick-action { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .quick-action:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0,0,0,.12) !important; }
        .min-w-0 { min-width: 0; }
      `}</style>
    </div>
  );
};
